import React, { useContext, useEffect, useState } from "react";
import "../PhotoUpload/PhotoUpload.scss";
import "./FileUpload.scss";
import plus from "@resources/images/upload-icon.svg";
import removePhoto from "@resources/images/white-cancel.svg";
import Hint from "@components/hint/HintComponent";
import AppService from "@service/AppService/AppService";
import { ReactComponent as Spinner } from "@resources/images/grid.svg";
import { getTranslatedTextIfNeeded } from "@shared/InternationalizationTextUtils";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { surveyQuestionMethods } from "../../shared/Questions/Questions";
import { generateFilename } from "../PhotoUpload/PhotoUtils";
import { getCategoryByRole } from "../../helper/fileCategory";
import { AuthContext } from "../../AuthContext";

const DEFAULT_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"];
const DEFAULT_MAX_MB = 15;
const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
const RESIZED_TARGET_HEIGHT = 300;

const normalizeExt = (ext) => (ext || "").toLowerCase().replace(/^\./, "");
const getExt = (filename) => {
    if (!filename) return "";
    // Strip query string and hash so that S3 URLs like
    // ".../abc.jpg?X-Amz-Signature=..." don't yield "jpg?x-amz-signature=..."
    return normalizeExt(filename.split(/[?#]/)[0].split(".").pop());
};
const isImageFilename = (filename) => IMAGE_EXTS.includes(getExt(filename));

function FileUpload({ className, readonly, question, onAnswer, selectedTopic }) {
    const authContext = useContext(AuthContext);
    const { t } = useTranslation();
    const [preview, setPreview] = useState(null);
    const [showLoader, setShowLoader] = useState(false);

    const allowedExtensions = (
        question.allowedFileExtensions && question.allowedFileExtensions.length > 0 ? question.allowedFileExtensions : DEFAULT_EXTENSIONS
    ).map(normalizeExt);
    const maxSizeMb = question.maxFileSizeMb || DEFAULT_MAX_MB;
    const allowedSize = maxSizeMb * 1024 * 1024;
    const acceptAttr = allowedExtensions.map((e) => "." + e).join(",");
    const extensionsLabel = allowedExtensions.join(", ").toUpperCase();

    useEffect(() => {
        if (!question.answers || question.answers.length === 0) return;
        const saved = question.answers[0].userAnswer;
        if (saved) {
            setPreview({ src: saved, isImage: isImageFilename(saved), name: null });
        }
    }, []);

    const validate = (file) => {
        const ext = getExt(file.name);
        if (!allowedExtensions.includes(ext)) {
            surveyQuestionMethods.showNotificationWithError(
                t("file-upload-invalid-type", {
                    types: extensionsLabel,
                    defaultValue: `Unsupported file type. Allowed: ${extensionsLabel}`
                })
            );
            return false;
        }
        if (file.size > allowedSize) {
            surveyQuestionMethods.showNotificationWithError(t("big-file-single"));
            return false;
        }
        return true;
    };

    const handleFiles = (files, answer) => {
        if (!files || files.length === 0) return;
        const file = files[0];
        if (!validate(file)) return;
        uploadFile(file, answer);
    };

    const onFileChange = (event, answer) => {
        event.preventDefault();
        const files = [...event.target.files];
        event.target.value = "";
        handleFiles(files, answer);
    };

    const onDropHandler = (event, answer) => {
        event.preventDefault();
        const files = [...event.dataTransfer.files];
        handleFiles(files, answer);
    };

    const dragStartHandler = (event) => event.preventDefault();
    const dragLeaveHandler = (event) => event.preventDefault();

    const createResizedFile = (file, filename) =>
        new Promise((resolve, reject) => {
            const objectUrl = URL.createObjectURL(file);
            const image = new Image();

            image.onload = () => {
                const scale = Math.min(1, RESIZED_TARGET_HEIGHT / image.height);

                if (scale === 1) {
                    URL.revokeObjectURL(objectUrl);
                    resolve(file);
                    return;
                }

                const canvas = document.createElement("canvas");
                const context = canvas.getContext("2d");
                const targetWidth = Math.round(image.width * scale);
                const targetHeight = Math.round(image.height * scale);

                if (!context) {
                    URL.revokeObjectURL(objectUrl);
                    reject(new Error("Canvas context is unavailable"));
                    return;
                }

                canvas.width = targetWidth;
                canvas.height = targetHeight;
                context.drawImage(image, 0, 0, targetWidth, targetHeight);

                canvas.toBlob(
                    (blob) => {
                        URL.revokeObjectURL(objectUrl);

                        if (!blob) {
                            reject(new Error("Image resize failed"));
                            return;
                        }

                        resolve(
                            new File([blob], filename, {
                                type: file.type,
                                lastModified: Date.now()
                            })
                        );
                    },
                    file.type,
                    0.92
                );
            };

            image.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error("Image load failed"));
            };

            image.src = objectUrl;
        });

    const uploadToS3 = (file, filename, userId, original, category) =>
        AppService.getFileUploadURL(filename, userId, original, category).then((resp) => {
            const uploadURL = resp?.data?.uploadURL;
            if (!uploadURL) {
                return Promise.reject(new Error("Missing upload URL"));
            }

            return fetch(uploadURL, { method: "PUT", body: file }).then((uploadResponse) => {
                if (uploadResponse.status !== 200) {
                    return Promise.reject(new Error("S3 upload failed"));
                }

                return uploadResponse;
            });
        });

    const uploadFile = (file, answer) => {
        setShowLoader(true);
        const filename = generateFilename(getExt(file.name));
        const userId = AppService.accountType === "admin" ? AppService.edId : authContext.user.id;
        const category = getCategoryByRole(authContext.user?.role);

        const uploadFlow = isImageFilename(file.name)
            ? createResizedFile(file, filename).then((resizedFile) =>
                  uploadToS3(file, filename, userId, true, category).then(() => uploadToS3(resizedFile, filename, userId, false, category))
              )
            : uploadToS3(file, filename, userId, true, category);

        uploadFlow
            .then(() => {
                createAnswer(filename, answer, file, category);
            })
            .catch(() => setShowLoader(false));
    };

    const createAnswer = (filename, answer, file, category) => {
        const value = `${category}/${filename}`;
        const res = AppService.uploadSingleFile(value, question.id, question.sfdcFieldId, answer.scoreValue);
        Promise.resolve(res)
            .then((result) => {
                if (result) {
                    const savedURL = result.data;
                    const answerIndex = question.answers.findIndex((a) => a.id === answer.id);
                    question.answers[answerIndex].userAnswer = savedURL;
                    onAnswer(selectedTopic, question, answer);
                    setPreview({
                        src: savedURL,
                        isImage: isImageFilename(file.name),
                        name: file.name
                    });
                }
                setShowLoader(false);
            })
            .catch(() => setShowLoader(false));
    };

    const onRemove = () => {
        const res = AppService.removeSingleFile(question.id);
        Promise.resolve(res)
            .then((result) => {
                if (result) {
                    question.answers[0].userAnswer = "";
                    onAnswer(selectedTopic, question, question.answers[0]);
                    setPreview(null);
                }
            })
            .catch(() => {});
    };

    const renderPreview = () => {
        if (!preview) return null;
        if (preview.isImage) {
            return (
                <div className="preview-container">
                    <div className="preview-wrapper">
                        <img src={preview.src} className="preview-photo" alt="preview" />
                        <img src={removePhoto} className="remove-photo" onClick={onRemove} alt="remove" />
                    </div>
                </div>
            );
        }
        return (
            <div className="file-preview">
                <div className="file-preview-info">
                    <div className="file-icon">PDF</div>
                    <a className="file-name" href={preview.src} target="_blank" rel="noreferrer">
                        {preview.name || t("file-upload-open", { defaultValue: "Open uploaded file" })}
                    </a>
                </div>
                <img src={removePhoto} className="file-remove" onClick={onRemove} alt="remove" />
            </div>
        );
    };

    const answers = question.answers.map((a, i) => (
        <div className="photo-upload file-upload" key={i}>
            {preview ? (
                renderPreview()
            ) : (
                <div
                    className="upload-container"
                    onDragStart={dragStartHandler}
                    onDragLeave={dragLeaveHandler}
                    onDragOver={dragStartHandler}
                    onDrop={(e) => onDropHandler(e, a)}
                >
                    {showLoader ? (
                        <div className="loading-spinner">
                            <Spinner />
                        </div>
                    ) : (
                        <label className="upload-label">
                            <input
                                type="file"
                                className="upload-input"
                                accept={acceptAttr}
                                onChange={(e) => onFileChange(e, a)}
                                alt="upload"
                            />
                            <div className="label-text">
                                <img src={plus} alt="add" />
                                <div className="title">
                                    <span className="blue-text">
                                        {t("file-upload-placeholder", { defaultValue: "Upload a file (images or PDF)" })}
                                    </span>
                                    , {t("or just drag and drop here")}
                                </div>
                                <div className="size-text">
                                    {t("file-upload-max-size", { size: maxSizeMb, defaultValue: `Max size: ${maxSizeMb} MB` })}
                                </div>
                                <div className="size-text types-text">
                                    {t("file-upload-allowed-types", {
                                        types: extensionsLabel,
                                        defaultValue: `Allowed: ${extensionsLabel}`
                                    })}
                                </div>
                            </div>
                        </label>
                    )}
                </div>
            )}
        </div>
    ));

    return (
        <div className={classNames("survey-question", className)}>
            {readonly ? <div className="answer-overlay" /> : null}
            <div className="question-text">{getTranslatedTextIfNeeded(question.text)}</div>
            <div className="question-answers upload-answer">
                <div className="content">{answers}</div>
            </div>
            <Hint hint={getTranslatedTextIfNeeded(question.hint)} />
        </div>
    );
}

export default FileUpload;
